import { CancelToken as CancelAxiosRequest } from 'axios';

import { wrapToOldRESTUrl } from '@services/api.service';
import { ApiServiceV1 } from '@services';

import { createDashboardModel } from '../_models/dashboard';

import { version as uiVersion } from '../../../../package.json';

let cancelDashboardRequest;

export const DashboardApiService = {
  async getMachineCategories({ suffix }) {
    const { data } = await ApiServiceV1.get(`${suffix}/machine-categories`);

    return (data || []).map(({ name }) => name);
  },

  async getDashboardItems({
    suffix, machineCategory: category,
    shippingPeriods = null,
    groupFilter = null,
    jobs = null,
  }) {
    if (cancelDashboardRequest) {
      cancelDashboardRequest('Canceled previous request to /dashboard');
    }

    const { data } = await ApiServiceV1.post(
      `${suffix}/dashboard`,
      {
        category, jobs, groupFilter, shippingPeriods,
      },
      {
        params: {
          v2: true,
        },
        cancelToken: new CancelAxiosRequest((c) => {
          cancelDashboardRequest = c;
        }),
      },
    );

    return data || createDashboardModel();
  },

  async getGroupJobs({
    machineCategory,
    operation,
    substrate,
    height,
    suffix,
    width,
  }) {
    const { data } = await ApiServiceV1.post(
      `${suffix}/group/job/${machineCategory}`,
      {
        operation,
        substrate,
        height,
        width,
      },
      {
        params: {
          v2: true,
        },
      },
    );

    return data || {};
  },

  async getQueueJobs({ suffix, queueItemId }) {
    const { data } = await ApiServiceV1.get(`${suffix}/queue/${queueItemId}/job`, {
      params: {
        v2: true,
      },
    });

    return data || {};
  },

  async getBasketJobs({ suffix, basketId }) {
    const { data } = await ApiServiceV1.get(`${suffix}/basket/${basketId}/jobs`, {
      params: {
        v2: true,
      },
    });

    return data || [];
  },

  markJobAsVip({ suffix, id, state: vip }) {
    return ApiServiceV1.post(`${suffix}/job/${id}/vip`, { vip }, {
      params: {
        v2: true,
      },
    });
  },

  splitQueue({
    splittedJobs,
    queueItemId,
    machine,
    suffix,
    jobs,
  }) {
    return ApiServiceV1.post(
      `${suffix}/queue/${machine}/split/${queueItemId}`,
      { splittedJobs, jobs },
      {
        params: {
          v2: true,
        },
      },
    );
  },

  sendGroupItemToMachine({ suffix, machine, ...data }) {
    return ApiServiceV1.post(
      `${suffix}/queue/${machine}/enqueue`,
      data,
      {
        params: {
          webUi: uiVersion,
          v2: true,
        },
      },
    );
  },

  moveQueueItemToMachine({
    targetMachine,
    criterionId,
    oldMachine,
    suffix,
  }) {
    return ApiServiceV1.post(
      `${suffix}/queue/${oldMachine}/move`,
      { criterionId, targetMachine },
      {
        params: {
          webUi: uiVersion,
          v2: true,
        },
      },
    );
  },

  moveAllQueueToMachine({ suffix, oldMachine, targetMachine }) {
    return ApiServiceV1.post(
      `${suffix}/queue/${oldMachine}/moveall`,
      { targetMachine },
      {
        params: {
          webUi: uiVersion,
          v2: true,
        },
      },
    );
  },

  moveQueueItemUp({ suffix, queueItemId, force = false }) {
    return ApiServiceV1.post(
      `${suffix}/queue/${queueItemId}/up`,
      {},
      {
        params: {
          force,
          webUi: uiVersion,
          v2: true,
        },
      },
    );
  },

  moveQueueItemDown({ suffix, queueItemId, force = false }) {
    return ApiServiceV1.post(
      `${suffix}/queue/${queueItemId}/down`,
      {},
      {
        params: {
          force,
          webUi: uiVersion,
          v2: true,
        },
      },
    );
  },

  returnQueueItemToGroup({ suffix, queueItemId }) {
    return ApiServiceV1.post(
      `${suffix}/queue/${queueItemId}/remove`,
      {},
      {
        params: {
          webUi: uiVersion,
          v2: true,
        },
      },
    );
  },

  beginBasket({ suffix, machine: machineName }) {
    return ApiServiceV1.post(
      wrapToOldRESTUrl(suffix, 'Basket/Begin'),
      {},
      {
        params: {
          machineName,
          webUi: uiVersion,
          v2: true,
        },
      },
    );
  },

  markBasketLoaded({ suffix, machine: machineName }) {
    return ApiServiceV1.post(
      wrapToOldRESTUrl(suffix, 'Basket/MarkLoaded'),
      {},
      {
        params: {
          machineName,
          webUi: uiVersion,
          v2: true,
        },
      },
    );
  },

  markBasketUnloaded({ suffix, basketId }) {
    return ApiServiceV1.post(
      wrapToOldRESTUrl(suffix, 'Basket/MarkUnloaded'),
      {},
      {
        params: {
          basketId,
          webUi: uiVersion,
          v2: true,
        },
      },
    );
  },

  processBasket({ suffix, basketId, jobs }) {
    return ApiServiceV1.post(
      wrapToOldRESTUrl(suffix, 'Basket/Process'),
      { jobs },
      {
        params: {
          basketId,
          webUi: uiVersion,
          v2: true,
        },
      },
    );
  },

  cancelBasket({ suffix, basketId }) {
    return ApiServiceV1.post(
      wrapToOldRESTUrl(suffix, 'Basket/Cancel'),
      {},
      {
        params: {
          basketId,
          webUi: uiVersion,
          v2: true,
        },
      },
    );
  },

  downloadJobsCsv({ suffix, machineCategory }) {
    return ApiServiceV1.get(`${suffix}/allActive/${machineCategory}`, {
      responseType: 'arraybuffer',
    });
  },
};
